import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import api from "@/lib/api";
import { RotateCw, Zap, Video, Image as ImageIcon, Mic, FolderOpen, Wand2, Key } from "lucide-react";

const TIERS = [
  { id: "free",    label: "Free Trial", desc: "2 watermarked downloads, hard cap" },
  { id: "weekly",  label: "Weekly",     desc: "30 downloads/week, watermark-free" },
  { id: "starter", label: "Starter",    desc: "60 downloads/mo, watermark-free" },
  { id: "pro",     label: "Pro",        desc: "120/mo, priority queue, brand kit" },
  { id: "premium", label: "Premium",    desc: "350/mo, Sora 2 Pro, white-label" },
];

const TEST_CHARGE_EMAIL = "kwaned7@gmail.com";

export default function OwnerConsolePage() {
  const { user, loading, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [banner, setBanner] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (user?.role === "owner") {
      api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    const tc = searchParams.get("test_charge");
    if (tc === "success") {
      setBanner({ kind: "success", text: "$1 test charge succeeded — live Stripe is wired correctly." });
    } else if (tc === "cancelled") {
      setBanner({ kind: "warn", text: "$1 test charge was cancelled before payment. No money was moved." });
    }
    if (tc) {
      searchParams.delete("test_charge");
      setSearchParams(searchParams, { replace: true });
    }
     
  }, []);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/owner" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;

  const active = user.impersonated_tier || "free";

  const impersonate = async (tier) => {
    setMsg(""); setBusy(true);
    try {
      await api.post("/admin/impersonate", { tier });
      await refresh();
      setMsg(`Now acting as ${tier === "free" ? "Free Trial" : tier} user.`);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const resetTrial = async () => {
    setMsg(""); setBusy(true);
    try {
      await api.post("/admin/reset-trial");
      await refresh();
      setMsg("Trial generation count reset to 0.");
    } catch (e) { setMsg(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const runTestCharge = async () => {
    setMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/stripe/test-charge");
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setMsg("Test charge session created but no checkout URL returned.");
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const isTestChargeOwner = (user?.email || "").toLowerCase() === TEST_CHARGE_EMAIL;

  return (
    <AdminShell
      title={{ eyebrow: "Owner Console · Private", heading: "Testing & Impersonation" }}
      subtitle="Switch your active tier to test the exact gating, watermark behavior and limits each plan sees. This only affects your account — real users are unaffected."
      breadcrumbs={[{ label: "Owner" }, { label: "Console" }]}
    >
      {banner && (
        <div
          data-testid="owner-test-charge-banner"
          className={`mb-6 px-4 py-3 border text-sm flex items-start gap-3 ${
            banner.kind === "success"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-300"
          }`}
        >
          <Zap className="w-4 h-4 mt-0.5" /> {banner.text}
        </div>
      )}

      {msg && <div className="mb-6 px-4 py-3 border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] text-sm" data-testid="owner-msg">{msg}</div>}

      {/* Quick admin actions */}
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-4">Admin Studio Shortcuts</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <ShortcutCard to="/owner/studio/video"     icon={Video}      label="Admin Video"     hint="Sora 2 Pro · private"     testId="admin-shortcut-video" />
          <ShortcutCard to="/owner/studio/image"     icon={ImageIcon}  label="Admin Image"     hint="Nano Banana · private"    testId="admin-shortcut-image" />
          <ShortcutCard to="/owner/studio/voiceover" icon={Mic}        label="Admin Voiceover" hint="OpenAI HD · private"      testId="admin-shortcut-voice" />
          <ShortcutCard to="/owner/renders"          icon={FolderOpen} label="Admin Library"   hint="Owner-only render history" testId="admin-shortcut-renders" />
          <ShortcutCard to="/owner/platform-keys"    icon={Key}        label="Platform Keys"   hint="Paste / rotate YBM provider keys" testId="admin-shortcut-platform-keys" />
        </div>
      </div>

      {/* Tier switcher */}
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-4">Acting as</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {TIERS.map((t) => (
            <button
              key={t.id}
              onClick={() => impersonate(t.id)}
              disabled={busy}
              data-testid={`impersonate-${t.id}`}
              className={`text-left p-5 border transition-colors disabled:opacity-50 ${
                active === t.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-[#2A2A2A] hover:border-[#D4AF37]"
              }`}
            >
              <div className="font-display text-xl font-bold mb-1 text-[#F4F4F0]">{t.label}</div>
              <div className="text-xs text-[#A0A0A0] leading-relaxed">{t.desc}</div>
              {active === t.id && (
                <div className="mt-3 text-[10px] uppercase tracking-wider text-[#D4AF37]">● Active</div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-10 flex flex-wrap gap-3">
        {isTestChargeOwner && (
          <button
            onClick={runTestCharge}
            disabled={busy}
            data-testid="owner-test-charge"
            className="inline-flex items-center gap-2 border border-emerald-500/60 hover:border-emerald-400 text-emerald-300 px-5 py-3 text-sm transition-colors disabled:opacity-50"
          >
            <Zap className="w-4 h-4" /> Run $1 Live Test Charge
          </button>
        )}
        <button
          onClick={resetTrial}
          disabled={busy}
          data-testid="owner-reset-trial"
          className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] px-5 py-3 text-sm transition-colors"
        >
          <RotateCw className="w-4 h-4" /> Reset Trial Counter (0/2)
        </button>
        <Link to="/dashboard" data-testid="owner-view-customer-app" className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] px-5 py-3 text-sm transition-colors">
          <Wand2 className="w-4 h-4" /> Preview Customer App (as {active})
        </Link>
      </div>

      {stats && (
        <div className="border border-[#2A2A2A] bg-[#121212] p-6 md:p-8" data-testid="owner-stats">
          <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-5">Platform Stats</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <Stat label="Users (total)" value={stats.users_total} />
            <Stat label="Paid subscribers" value={stats.users_paid} />
            <Stat label="Successful payments" value={stats.successful_payments} />
            <Stat label="Renders (total)" value={stats.renders_total} />
            <Stat label="Videos generated" value={stats.renders_video} />
            <Stat label="Images generated" value={stats.renders_image} />
          </div>
        </div>
      )}

      <div className="mt-12 text-xs text-[#A0A0A0]">
        Current state: <span className="font-mono text-[#F4F4F0]">role={user.role}</span> ·{" "}
        <span className="font-mono text-[#F4F4F0]">impersonated_tier={user.impersonated_tier || "—"}</span> ·{" "}
        <span className="font-mono text-[#F4F4F0]">subscription_tier={user.subscription_tier || "—"}</span> ·{" "}
        <span className="font-mono text-[#F4F4F0]">trial_used={user.trial_generations_used}</span>
      </div>
    </AdminShell>
  );
}

function ShortcutCard({ to, icon: Icon, label, hint, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group block p-5 border border-[#D4AF37]/30 bg-[#121212] hover:bg-[#D4AF37]/5 transition-colors"
    >
      <Icon className="w-5 h-5 text-[#D4AF37] mb-3" strokeWidth={1.5} />
      <div className="font-display text-lg font-bold text-[#F4F4F0] mb-1">{label}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{hint}</div>
    </Link>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <div className="font-display text-3xl font-extrabold text-[#F4F4F0]">{value}</div>
    </div>
  );
}
